"use client";

/**
 * CookieConsentBanner — GDPR-compliant consent banner.
 *
 * Shows on first visit when no consent cookie exists.
 * Essential cookies are always allowed; analytics/marketing require opt-in.
 * Does NOT break SSR/SEO — renders client-only with no layout shift.
 *
 * Reference: site/docs/audits/MAPPITO_WEBSITE_PLAN.md §15.9
 */

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import {
  getConsentState,
  acceptAllConsent,
  acceptEssentialOnly,
  type ConsentState,
} from "@/lib/consent";

export default function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);
  const acceptAllRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Show banner only if no consent decision has been made
    const state = getConsentState();
    if (!state) {
      // Small delay to avoid layout flash
      const timer = setTimeout(() => setVisible(true), 500);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, []);

  // Move focus to the accept button when banner becomes visible
  useEffect(() => {
    if (visible && acceptAllRef.current) {
      acceptAllRef.current.focus();
    }
  }, [visible]);

  if (!visible) return null;

  function handleAcceptAll() {
    const state: ConsentState = acceptAllConsent();
    setVisible(false);
    // Notify analytics provider that consent was given
    window.dispatchEvent(
      new CustomEvent("consent-updated", { detail: state }),
    );
    // Reload to initialize analytics scripts
    window.location.reload();
  }

  function handleEssentialOnly() {
    acceptEssentialOnly();
    setVisible(false);
  }

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      aria-live="polite"
      className="fixed bottom-0 left-0 right-0 z-50 border-t border-gray-200 bg-white p-4 shadow-lg sm:p-6"
    >
      <div className="mx-auto max-w-4xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex-1">
            <p className="text-sm text-gray-700">
              We use cookies to improve your experience and analyze site traffic.
              Essential cookies are always active. You can choose to accept
              analytics cookies to help us improve Mappito.{" "}
              <Link
                href="/en/privacy"
                className="text-blue-600 underline hover:text-blue-700"
              >
                Privacy Policy
              </Link>
            </p>
          </div>
          <div className="flex shrink-0 gap-3">
            <button
              type="button"
              onClick={handleEssentialOnly}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Essential Only
            </button>
            <button
              type="button"
              onClick={handleAcceptAll}
              ref={acceptAllRef}
              className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
